<template>
  <v-form ref="formRef" class="lighting-form method-form" @submit.prevent="">
    <!-- Betriebszeit -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebszeit</span>
      </div>

      <!-- Manual Uptime -->
      <v-checkbox
        v-model="form.useManualUptime"
        :error-messages="validationErrors?.use_manual_uptime"
        label="Manuelle Brenndauer"
        :disabled="!sharedData.zone"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.use_manual_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.use_manual_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.use_manual_uptime"
            icon
            @click="copyFromReference('use_manual_uptime')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Uptime -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Brenndauer"
        suffix="h/a"
        :disabled="!sharedData.zone || !form.useManualUptime"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            @click="copyFromReference('uptime')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Zonenprofil nach DIN V 18599-4, 5.3, für Zonen ohne Eintrag in
           DIN V 18599-10, Anhang A — neun Industrie-Schichtzonen, beide
           Parkplatzzonen und „Sonstige“. Ohne diesen Weg wären sie für die neue
           Rechenmethode dauerhaft gesperrt. -->
      <v-checkbox
        v-model="form.zoneProfileManual"
        :error-messages="validationErrors?.zone_profile_manual"
        label="Zonenprofil manuell (DIN V 18599-4, 5.3)"
        hint="Für Nutzungszonen ohne Profil in DIN V 18599-10, Anhang A: Betriebszeiten und Faktoren selbst ermitteln."
        persistent-hint
        class="mb-3"
      />

      <template v-if="form.zoneProfileManual">
        <v-text-field
          v-model.number="form.manualTDayH"
          type="number"
          min="0"
          max="4380"
          label="Nutzungsstunden zur Tagzeit"
          suffix="h/a"
          :error-messages="validationErrors?.manual_t_day_h"
          class="mb-3 ml-8"
        />
        <v-text-field
          v-model.number="form.manualTNightH"
          type="number"
          min="0"
          max="8760"
          label="Nutzungsstunden zur Nachtzeit"
          suffix="h/a"
          :error-messages="validationErrors?.manual_t_night_h"
          class="mb-3 ml-8"
        />
        <v-text-field
          v-model.number="form.manualFT"
          type="number"
          min="0"
          max="1"
          step="0.05"
          label="Minderungsfaktor Gebäudebetriebszeit"
          :error-messages="validationErrors?.manual_f_t"
          hint="F_t — Anteil der Betriebszeit, in dem beleuchtet wird."
          persistent-hint
          class="mb-3 ml-8"
        />
        <v-text-field
          v-model.number="form.manualCAbsence"
          type="number"
          min="0"
          max="1"
          step="0.05"
          label="Relative Abwesenheit"
          :error-messages="validationErrors?.manual_c_absence"
          hint="C_A — ohne Präsenzmelder ohne Wirkung auf das Ergebnis."
          persistent-hint
          class="mb-3 ml-8"
        />
      </template>
    </div>

    <!-- Leuchte / Lampe -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-lightbulb-on-outline</v-icon>
        <span>Leuchte / Lampe</span>
      </div>

      <!-- Free choice of luminaire — lifts the zone filter on the catalogue (WP-08) -->
      <v-checkbox
        v-model="form.freeChoiceLamp"
        :error-messages="validationErrors?.free_choice_lamp"
        label="Freie Wahl der Leuchte"
        :disabled="!sharedData.change"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.free_choice_lamp" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.free_choice_lamp }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.free_choice_lamp"
            icon
            @click="copyFromReference('free_choice_lamp')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Lamp Or Light -->
      <v-autocomplete
        v-model="form.lampOrLight"
        :error-messages="validationErrors?.lamp_or_light_qry"
        label="Leuchte"
        :items="lampsOrLights"
        :disabled="!sharedData.change"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.lamp_or_light_qry" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.lamp_or_light_qry }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.lamp_or_light_qry"
            icon
            @click="copyFromReference('lamp_or_light_qry')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Amount -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :disabled="!form.lampOrLight"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            @click="copyFromReference('amount')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Freie Leistung — dasselbe Muster wie „Freie Wahl der Leuchte“ darüber:
           Auswahlliste als Vorgabe, bewusster Haken als Ausweg. Der Katalog garantiert,
           dass Leuchte, Lampenzahl und Leistung zusammenpassen; ein Bestandsaudit misst
           aber auch Leistungen, die er nicht führt.

           Ausgeblendet, sobald die Systemleistung laut Hersteller gilt: dort steht die
           Leistung der ganzen Leuchte im Datensatz, eine zweite freie Zahl je Lampe
           wäre nur eine Falle. -->
      <v-checkbox
        v-if="form.powerBasis !== 'system'"
        v-model="form.useManualPower"
        :error-messages="validationErrors?.use_manual_power"
        label="Freie Leistung"
        hint="Leistung je Lampe selbst eintragen, statt sie aus dem Katalog zu wählen. Die Verlustleistung des Betriebsgeräts wird weiterhin addiert."
        persistent-hint
        class="mb-3 ml-8"
      />

      <!-- Power, frei eingetragen -->
      <v-text-field
        v-if="form.useManualPower && form.powerBasis !== 'system'"
        v-model="form.power"
        type="number"
        min="0"
        step="0.5"
        :error-messages="validationErrors?.power_qry || validationErrors?.manual_power"
        label="Leistung je Lampe"
        suffix="W"
        :disabled="!form.amount"
        validate-on-blur
        class="mb-3 ml-8"
      />

      <!-- Power, aus dem Katalog -->
      <v-autocomplete
        v-else
        v-model="form.power"
        :error-messages="validationErrors?.power_qry"
        label="Leistung je Lampe"
        suffix="W"
        :items="powers"
        :disabled="!form.amount"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.power_qry" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.power_qry }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.power_qry"
            icon
            @click="copyFromReference('power_qry')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Light per lamp -->
      <v-autocomplete
        v-model="form.lightPerLamp"
        :error-messages="validationErrors?.light_per_lamp"
        label="Lampen je Leuchte"
        :items="lightPerLamps"
        :disabled="!form.power"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.light_per_lamp" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.light_per_lamp }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.light_per_lamp"
            icon
            @click="copyFromReference('light_per_lamp')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Technische Ausstattung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-lightning-bolt-outline</v-icon>
        <span>Technische Ausstattung</span>
      </div>

      <!-- Ermittlung der Systemleistung, DIN V 18599-4, 5.4.5 -->
      <v-select
        v-model="form.powerBasis"
        :error-messages="validationErrors?.power_basis"
        label="Ermittlung der Systemleistung"
        :items="powerBasisOptions"
        item-title="label"
        item-value="value"
        :hint="powerBasisHint"
        persistent-hint
        class="mb-3"
      />

      <v-text-field
        v-if="form.powerBasis === 'system'"
        v-model.number="form.systemPowerW"
        type="number"
        min="0"
        step="0.5"
        label="Systemleistung je Leuchte (W)"
        :error-messages="validationErrors?.system_power_w"
        hint="Gesamte Leistungsaufnahme einer Leuchte inklusive Betriebsgerät — vom Datenblatt oder gemessen."
        persistent-hint
        class="mb-3 ml-8"
      />

      <!-- Ballast -->
      <v-autocomplete
        v-model="form.ballast"
        :error-messages="validationErrors?.ballast"
        label="Vorschaltgerät"
        :items="groupedBallastItems"
        item-title="label"
        item-value="id"
        :disabled="!form.lightPerLamp"
        :hint="selectedBallastNote"
        persistent-hint
        validate-on-blur
        class="mb-3"
      >
        <!-- Zeile in der Liste: Kürzel und Name oben, Verlustleistung und
             Steuerbarkeit darunter. Beides stand bereits im Datensatz, war aber
             nirgends zu sehen. -->
        <template #item="{ props: itemProps, item }">
          <v-list-item v-bind="itemProps" :title="item.raw.label">
            <template #subtitle>
              <span>{{ item.raw.powerNote }}</span>
              <span v-if="item.raw.powerNote && item.raw.controlNote"> · </span>
              <span>{{ item.raw.controlNote }}</span>
            </template>
          </v-list-item>
        </template>

        <template #append>
          <v-tooltip v-if="hint.ballast" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.ballast }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.ballast"
            icon
            @click="copyFromReference('ballast')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Ballast per lamp -->
      <v-autocomplete
        v-model="form.ballastPerLamp"
        :error-messages="validationErrors?.ballast_per_lamp"
        label="Vorschaltgeräte je Leuchte"
        :items="ballastPerLamps"
        :disabled="!form.ballast"
        :hint="ballastLossHint"
        persistent-hint
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.ballast_per_lamp" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.ballast_per_lamp }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.ballast_per_lamp"
            icon
            @click="copyFromReference('ballast_per_lamp')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Regelung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-tune-variant</v-icon>
        <span>Regelung</span>
      </div>

      <!-- Legacy control — only for records stored on calc stand 1 or 2 (WP-C2).
           From stand 3 on, calc() ignores this field entirely; leaving it editable
           would suggest an effect it no longer has. -->
      <v-alert
        v-if="isOldMethod"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        Dieser Datensatz wurde mit der <strong>alten Rechenmethode</strong> angelegt
        ({{ form.calcMethodLabel }}) und rechnet weiter mit deren Regelung. Die Steuerung
        nach DIN V 18599-4 gehört zur neuen Rechenmethode und wirkt hier nicht.
      </v-alert>

      <v-autocomplete
        v-if="isOldMethod"
        v-model="form.control"
        :error-messages="validationErrors?.control"
        label="Regelung"
        :items="controls"
        item-title="name"
        item-value="id"
        readonly
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.control" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.control }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.control"
            icon
            @click="copyFromReference('control')"
            title="Referenzwert übernehmen"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Steuerung nach DIN V 18599-4 (WP-C1) -->
      <template v-if="!isOldMethod">
        <v-checkbox
          v-model="form.controlPresence"
          :error-messages="validationErrors?.control_presence"
          label="Präsenzmelder"
          :hint="hint.control_presence"
          persistent-hint
          class="mb-3"
        />

        <v-select
          v-model="form.daylightSupply"
          :error-messages="validationErrors?.daylight_supply"
          label="Tageslichtversorgung"
          :items="daylightSupplyOptions"
          item-title="label"
          item-value="value"
          :hint="hint.daylight_supply"
          persistent-hint
          class="mb-3"
        />

        <!-- Everything below only bites once daylight is available. -->
        <template v-if="form.daylightSupply !== 'keine'">
          <v-select
            v-model="form.controlDaylight"
            :error-messages="validationErrors?.control_daylight"
            label="Tageslichtabhängige Steuerung"
            :items="daylightControlOptions"
            item-title="label"
            item-value="value"
            :item-props="item => ({ disabled: item.disabled })"
            :hint="selectedBallastIsDimmable
              ? undefined
              : 'Gedimmte Stufen setzen ein regelbares Betriebsgerät voraus (DIN V 18599-4, 5.4.6). Das gewählte Gerät kann nur schalten.'"
            :persistent-hint="!selectedBallastIsDimmable"
            class="mb-3 ml-8"
          />

          <v-text-field
            v-model.number="form.daylightAreaShare"
            type="number"
            step="0.05"
            min="0"
            max="1"
            label="Anteil tageslichtversorgte Fläche"
            :error-messages="validationErrors?.daylight_area_share"
            :hint="hint.daylight_area_share"
            class="mb-3 ml-8"
          />
        </template>

        <v-checkbox
          v-model="form.constantLight"
          :error-messages="validationErrors?.constant_light"
          label="Konstantlichtregelung"
          :disabled="!selectedBallastIsDimmable"
          :hint="selectedBallastIsDimmable
            ? hint.constant_light
            : 'Setzt ein regelbares Betriebsgerät voraus (DIN V 18599-4, 5.4.6).'"
          persistent-hint
          class="mb-3"
        />

        <!-- Details: measured spread 0,3 % (orientation) to 45 % (glazing, and only
             for Sonnenschutzglas). Defaults are adequate for the ordinary case. -->
        <v-expansion-panels variant="accordion" class="mb-2">
          <v-expansion-panel title="Details nach DIN V 18599-4">
            <v-expansion-panel-text>
              <v-select
                v-model="form.daylightOpening"
                label="Art der Tageslichtöffnung"
                :items="daylightOpeningOptions"
                item-title="label"
                item-value="value"
                class="mb-3"
              />

              <v-select
                v-model="form.orientation"
                label="Orientierung"
                :items="orientationOptions"
                item-title="label"
                item-value="value"
                class="mb-3"
              />

              <v-select
                v-if="form.daylightOpening === 'dachoberlicht'"
                v-model.number="form.roofInclination"
                label="Neigung der Dachöffnung"
                :items="roofInclinationOptions"
                item-title="label"
                item-value="value"
                class="mb-3"
              />

              <template v-else>
                <v-select
                  v-model.number="form.glazingTauD65"
                  label="Verglasung"
                  :items="glazingOptions"
                  item-title="label"
                  item-value="tau_d65"
                  class="mb-3"
                />

                <v-select
                  v-model="form.sunShading"
                  label="Sonnen-/Blendschutz"
                  :items="sunShadingOptions"
                  item-title="label"
                  item-value="value"
                  class="mb-3"
                />
              </template>

              <v-select
                v-model.number="form.maintenanceFactor"
                label="Wartungsfaktor"
                :items="maintenanceFactorOptions"
                item-title="label"
                item-value="value"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </div>
  </v-form>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import {
  ballastLabel,
  ballastPowerNote,
  ballastControlNote,
  ballastGroup,
  ballastLossSummary,
  BALLAST_GROUP_LEGACY,
  BALLAST_GROUP_CONTROLLABLE,
} from '@/utils/ballast'
import api from '@/plugins/axios'

/* Props */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference: { type: Boolean, default: false },
  planned: { type: Boolean, default: false },
  sharedData: { type: Object, default: () => ({}) },
  otherData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Pinia */

/* Form fields mapping */
const fields = {
  actionId: 'action_id',
  useManualUptime: 'use_manual_uptime',
  uptime: 'uptime',
  calcMethod: 'calc_method',
  calcMethodLabel: 'calc_method_label',
  lampOrLight: 'lamp_or_light_qry',
  freeChoiceLamp: 'free_choice_lamp',
  amount: 'amount',
  daylightOpening: 'daylight_opening',
  roofInclination: 'roof_inclination',
  daylightSupply: 'daylight_supply',
  daylightAreaShare: 'daylight_area_share',
  controlDaylight: 'control_daylight',
  controlPresence: 'control_presence',
  constantLight: 'constant_light',
  glazingTauD65: 'glazing_tau_d65',
  sunShading: 'sun_shading',
  orientation: 'orientation',
  maintenanceFactor: 'maintenance_factor',
  power: 'power_qry',
  lightPerLamp: 'light_per_lamp',
  ballast: 'ballast',
  ballastPerLamp: 'ballast_per_lamp',
  control: 'control',
  powerBasis: 'power_basis',
  systemPowerW: 'system_power_w',
  useManualPower: 'use_manual_power',
  zoneProfileManual: 'zone_profile_manual',
  manualTDayH: 'manual_t_day_h',
  manualTNightH: 'manual_t_night_h',
  manualFT: 'manual_f_t',
  manualCAbsence: 'manual_c_absence',
}

/* DIN V 18599-4, 5.4.5 — die drei Wege zur Systemleistung, in der Reihenfolge, in der
   die Norm sie nennt. Die Vorgabe bleibt der gewachsene Weg, damit kein gespeichertes
   Ergebnis wandert; die Beschriftung sagt, welcher der Vorzugsweg ist. */
const powerBasisOptions = [
  { value: 'system', label: 'Systemleistung laut Hersteller oder Messung (Vorzugsweg)' },
  { value: 'k_bg', label: 'Tabelle 8 (k_BG) aus der Lampenleistung' },
  { value: 'ballast', label: 'Lampenleistung plus Verlustleistung je Gerät' },
]

/* Composable integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  // A checkbox must never bind to null — the backend field is non-nullable and an
  // unchecked box has to round-trip as `false`, not as "absent".
  // Die 18599-Auswahlfelder brauchen dieselben Vorgaben wie das Modell, sonst postet
  // ein frisches Formular `null` statt des Normvorgabewerts.
  defaults: {
    freeChoiceLamp: false,
    daylightOpening: 'fassade',
    roofInclination: 0,
    daylightSupply: 'keine',
    daylightAreaShare: 1.0,
    controlDaylight: 'manuell',
    controlPresence: false,
    constantLight: false,
    glazingTauD65: 0.82,
    sunShading: 'blendschutz',
    orientation: 'ost_west',
    maintenanceFactor: 0.67,
    powerBasis: 'ballast',
    zoneProfileManual: false,
    useManualPower: false,
  },
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => false), // not a shared form
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Template list data */
const lampsOrLights = ref([])
const powers = ref([])
const lightPerLamps = ref([])
const ballasts = ref([])
const ballastPerLamps = ref([])
const controls = ref([])
const zones = ref([])

/* Shared Data Helpers */
const zone = computed(() => props.sharedData?.zone)
const change = computed(() => props.sharedData?.change)

/* --- Steuerung nach DIN V 18599-4 (WP-C1/C2) ------------------------------ */

/* Ein mit der alten Rechenmethode angelegter Datensatz rechnet weiter über `control`.
   Für ihn bleibt die Altsteuerung sichtbar — schreibgeschützt, damit klar ist,
   dass sie zu diesem Datensatz gehört und nicht mehr zum Verfahren.

   Gefragt wird nach der Methode, nicht nach einer Standnummer: die Nummer ist
   Buchführung des Backends, hier zählt nur alt gegen neu. */
const isOldMethod = computed(() => form.calcMethod === 'alt')

/* Das gewählte Gerät, für Beschriftung und Rechnung weiter unten. */
const selectedBallast = computed(
  () => ballasts.value.find(b => b.id === form.ballast) ?? null)

/* `constant_light` ist nach 5.4.6 nur mit regelbarem Betriebsgerät wirksam.
   Die Dimmbarkeit steht am gewählten Vorschaltgerät (WP-08). */
const selectedBallastIsDimmable = computed(() => !!selectedBallast.value?.dimmable)

/* Auswahlliste: Kürzel **und** ausgeschriebener Name, nach Bestand/steuerbar gruppiert.
   Bis hierher zeigte die Liste nur das Kürzel — „HFD“ ohne jeden weiteren Anhalt —,
   obwohl `full_name` schon in der Antwort des Endpunkts stand. */
const ballastItems = computed(() => ballasts.value.map(b => ({
  ...b,
  label: ballastLabel(b),
  powerNote: ballastPowerNote(b),
  controlNote: ballastControlNote(b),
  group: ballastGroup(b),
})))

/* Vuetify zeigt Gruppenüberschriften über `:items` mit Trennern. Bestandsgeräte zuerst:
   die Bestandsvariante wird zuerst erfasst. Bei nur einer Gruppe entfällt die
   Überschrift — bei einer Lampe mit genau einem zulässigen Gerät wäre sie Lärm. */
const groupedBallastItems = computed(() => {
  const groups = [BALLAST_GROUP_LEGACY, BALLAST_GROUP_CONTROLLABLE]
  const present = groups.filter(g => ballastItems.value.some(b => b.group === g))
  if (present.length < 2) return ballastItems.value

  return present.flatMap(g => [
    { type: 'subheader', title: g },
    ...ballastItems.value.filter(b => b.group === g),
  ])
})

/* Was der gewählte Weg nach 5.4.5 bedeutet — und, beim gewachsenen Verfahren, dass er
   keiner der beiden Normwege ist. Das steht hier und nicht nur in der Hilfe, weil die
   Wahl das Ergebnis um mehr als 20 % verschiebt. */
const powerBasisHint = computed(() => ({
  system: 'Vorzugsweg der Norm: Lampenleistung, Lampen je Leuchte und Verlustleistung '
    + 'gehen dann nicht mehr ein — die Angabe enthält sie bereits.',
  k_bg: 'Vereinfachte Ermittlung nach Tabelle 8: Lampenleistung × k_BG. Die Faktoren '
    + 'gelten für mittlere Lampenleistungen und sind an den Rändern entsprechend grob.',
  ballast: 'Bisheriges EARNS-Verfahren, kein Weg der Norm. Bleibt Vorgabe, damit '
    + 'gespeicherte Ergebnisse unverändert bleiben.',
}[form.powerBasis] ?? ''))

/* Unter dem geschlossenen Feld: was am gewählten Gerät hängt. Ohne Auswahl bleibt die
   Zeile leer, statt Platz für einen Hinweis zu reservieren, den es noch nicht gibt. */
const selectedBallastNote = computed(() => {
  const chosen = selectedBallast.value
  if (!chosen) return ''
  return [ballastPowerNote(chosen), ballastControlNote(chosen)]
    .filter(Boolean)
    .join(' · ')
})

/* Was die Wahl real kostet — „13 W × 2 Geräte = 26 W je Leuchte, bei 40 Leuchten
   1,04 kW“. Die Hilfeseite bat bislang mit Ausrufezeichen darum, die Geräte
   durchzuzählen; eine mitlaufende Zahl macht das von selbst sichtbar. */
const ballastLossHint = computed(() => ballastLossSummary(
  selectedBallast.value, form.ballastPerLamp, form.amount))

const daylightOpeningOptions = [
  { value: 'fassade', label: 'vertikale Fassade' },
  { value: 'dachoberlicht', label: 'Dachoberlicht' },
]

const daylightSupplyOptions = [
  { value: 'keine', label: 'keine Tageslichtversorgung' },
  { value: 'gering', label: 'gering' },
  { value: 'mittel', label: 'mittel' },
  { value: 'gut', label: 'gut' },
]

/* Die beiden gedimmten Stufen setzen ein regelbares Betriebsgerät voraus — dieselbe
   Regel, die `validate_ballast_drives_control` für die Altsteuerung durchsetzt (WP-08 /
   Befund V1). Sie wird hier gesperrt statt versteckt: wer „gedimmt, ausschaltend“ sucht
   und nicht findet, soll den Grund lesen können, statt die Stufe für nicht vorhanden zu
   halten. Gleiches Muster wie bei der Konstantlichtregelung darunter. */
const daylightControlOptions = computed(() => {
  const dimmable = selectedBallastIsDimmable.value
  return [
    { value: 'manuell', label: 'manuell' },
    { value: 'gedimmt_nicht_aus', label: 'gedimmt, nicht ausschaltend', needsDimming: true },
    { value: 'gedimmt_aus', label: 'gedimmt, ausschaltend', needsDimming: true },
  ].map(o => ({ ...o, disabled: !!o.needsDimming && !dimmable }))
})

/* „Freie Leistung“ verschwindet mit der Systemleistung — der Haken darf dann nicht
   unsichtbar gesetzt bleiben, sonst rechnete ein späterer Wechsel zurück auf einen
   Wert, den niemand mehr gesehen hat. */
watch(() => form.powerBasis, (basis) => {
  if (basis === 'system') form.useManualPower = false
})

/* Wechselt die Auswahl auf ein nicht regelbares Gerät, während eine gedimmte Stufe
   gesetzt ist, bliebe sonst ein Wert stehen, den das Formular nicht mehr anbietet. */
watch(selectedBallastIsDimmable, (dimmable) => {
  if (!dimmable && form.controlDaylight?.startsWith('gedimmt')) {
    form.controlDaylight = 'manuell'
  }
})

const orientationOptions = [
  { value: 'sued', label: 'Süd' },
  { value: 'ost_west', label: 'Ost/West' },
  { value: 'nord', label: 'Nord' },
]

const roofInclinationOptions = [
  { value: 0, label: 'horizontal (0°)' },
  { value: 30, label: '30°' },
  { value: 45, label: '45°' },
  { value: 60, label: '60°' },
  { value: 90, label: '90°' },
]

const sunShadingOptions = [
  { value: 'blendschutz', label: 'nur Blendschutz' },
  { value: 'automatisch', label: 'automatisch betrieben' },
  { value: 'lichtlenkend', label: 'lichtlenkende Systeme' },
  { value: 'keiner', label: 'kein Sonnen- oder Blendschutz' },
]

const maintenanceFactorOptions = [
  { value: 0.80, label: '0,80 — sehr saubere Räume, häufig gereinigte Leuchten' },
  { value: 0.67, label: '0,67 — Regelfall' },
  { value: 0.60, label: '0,60 — hohe Staubbelastung, seltene Reinigung' },
  { value: 0.50, label: '0,50 — zusätzlich überlange Wartungsintervalle' },
]

/* Tabelle 11 kommt vom Server, damit die siebzehn Zeilen nicht doppelt gepflegt
   werden müssen — siehe `…/lighting2/glazing/`. */
const glazingOptions = ref([])

async function fetchGlazing() {
  try {
    const { data } = await api.get('/methods/lighting2/glazing/')
    glazingOptions.value = data
  } catch {}
}

/* Watchers */
watch(zone, async () => {
  if (!zone.value) return

  const z = zones.value.find(x => x.id === zone.value)
  if (z) form.uptime = z.norm_uptime

  // ensure boolean behavior
  form.useManualUptime = !!form.useManualUptime

  await fetchLampOrLights()
}, { immediate: true })

watch(() => form.lampOrLight, fetchPowers, { immediate: true })
watch(change, fetchLampOrLights, { immediate: true })
watch(() => form.power, fetchBallasts, { immediate: true })
watch(() => form.lightPerLamp, setBallastPerLampsOptions, { immediate: true })

// Widening or narrowing the catalogue restarts the luminaire cascade (WP-08).
watch(() => form.freeChoiceLamp, fetchLampOrLights)

// The gear decides which controls are physically available (WP-08 / finding V1).
// `immediate` so a stored record is narrowed on load, not only after an edit.
watch(() => form.ballast, fetchControls, { immediate: true })

/* API Calls */
async function fetchZones() {
  try {
    const { data } = await api.get('/methods/lighting2/zone/')
    zones.value = data
  } catch {}
}

async function fetchLampOrLights() {
  if (!zone.value) return

  try {
    const { data } = await api.get('/methods/lighting2/choices/', {
      params: {
        reference: props.reference,
        zone: zone.value,
        change: change.value,
        free_choice_lamp: !!form.freeChoiceLamp,
      },
    })

    lampsOrLights.value =
      change.value === 'bulb'
        ? data.light
        : data.lamp
  } catch {}
}

async function fetchPowers() {
  if (!zone.value || !change.value || !form.lampOrLight) return

  try {
    const { data } = await api.get('/methods/lighting2/choices/', {
      params: {
        reference: props.reference,
        zone: zone.value,
        change: change.value,
        lamp_or_light: form.lampOrLight,
        // The override has to travel through every step of the cascade. Without it the
        // zone filter would reapply here and a freely chosen luminaire would come back
        // with no available power ratings.
        free_choice_lamp: !!form.freeChoiceLamp,
      },
    })

    powers.value = data.power.map(x => x.toString())

    if (powers.value.length === 1) {
      form.power = powers.value[0]
    }
  } catch {}
}

async function fetchBallasts() {
  if (!zone.value || !change.value || !form.lampOrLight || !form.power) return

  try {
    const { data } = await api.get('/methods/lighting2/choices/', {
      params: {
        reference: props.reference,
        zone: zone.value,
        change: change.value,
        lamp_or_light: form.lampOrLight,
        power: form.power,
        free_choice_lamp: !!form.freeChoiceLamp,
      },
    })

    lightPerLamps.value = data.amount

    if (lightPerLamps.value.length === 1) {
      form.lightPerLamp = lightPerLamps.value[0]
    }

    await fetchBallastDetails(data.ballast)
  } catch {}
}

async function fetchBallastDetails(ballastIds = []) {
  try {
    const { data } = await api.get('/methods/lighting2/ballast/')
    ballasts.value = data.filter(x => ballastIds.includes(x.id))

    if (ballasts.value.length === 1) {
      form.ballast = ballasts.value[0].id
    }
  } catch {}
}

function setBallastPerLampsOptions() {
  ballastPerLamps.value = []
  for (let i = 1; i <= form.lightPerLamp; i++) {
    ballastPerLamps.value.push(i)
  }
  if (ballastPerLamps.value.length === 1) {
    form.ballastPerLamp = ballastPerLamps.value[0]
  }
}

async function fetchControls() {
  try {
    const { data } = await api.get('/methods/lighting2/controls/', {
      // No ballast chosen yet: ask for everything, so the list is not mysteriously
      // short while the form is still being filled in.
      params: { ballast: form.ballast || undefined },
    })
    controls.value = data

    // Drop a selection the newly chosen gear cannot drive. Leaving it would show a
    // control that is not in the list and fail validation on save with no visible
    // cause — the picker has to agree with the rule the serializer enforces.
    if (form.control && !data.some(c => c.id === form.control)) {
      form.control = null
    }

    if (controls.value.length === 1) form.control = controls.value[0].id
  } catch {}
}

/* Lifecycle */
onMounted(async () => {
  await fetchZones()
  await fetchGlazing()
  await fetchLampOrLights()
  // fetchControls() is driven by the `form.ballast` watcher above, which runs
  // immediately — calling it again here would only duplicate the request.
  await fetchHints()
})

</script>

<style scoped lang="scss">
.lighting-form {
  width: 100%;
}

/* Field styling */
.lighting-form :deep(.v-field) {
  border-radius: 18px;
}

.lighting-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.lighting-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.lighting-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.lighting-form :deep(.v-input__append),
.lighting-form :deep(.v-field__append-inner) {
  align-items: center;
}

.lighting-form :deep(.v-input__append .v-icon),
.lighting-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.lighting-form :deep(.v-input__append .v-btn),
.lighting-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.lighting-form :deep(.v-input__append .v-btn:hover),
.lighting-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled fields */
.lighting-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .lighting-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
